/**
 * Turning the portfolio's four totals into things a sponsor can decide.
 *
 * The totals say something is wrong everywhere and nothing about where, so the only honest reply
 * to them is to open every contract. This folds the rows behind them onto their cause: one line per
 * cause, what it accounts for, how it is spread, and what goes wrong if nobody acts.
 *
 * The delivery model forbids ranking people (`docs/2026-09-20-program-structure.md`, "Roll-ups":
 * counts, never people, never ranks). Causes are things, so they may be ordered; the order is by
 * consequence and the page states it. The only name on a cause is the role holder who owns it,
 * which is the same disclosure the portfolio table already makes, and there is never a figure
 * against a person.
 */
import type { CadenceMiss, PortfolioLine, PortfolioView } from "./portfolio.js";
import type { ProgramId } from "./commitment.js";
import { plural } from "./dates.js";

export type TriageKind = "ask" | "contract" | "role" | "overdue" | "cadence";

/** Ascending: what the customer can see, then the contract, then accountability, then work, then rhythm. */
const RANK: Record<TriageKind, number> = { ask: 0, contract: 1, role: 2, overdue: 3, cadence: 4 };

export const TRIAGE_RULE =
  "Ordered by what goes wrong if nobody acts: what the customer can already see, then the contract, "
  + "then who is accountable, then promised work, then the rhythm. Causes are ordered; people are counted "
  + "and named only where they hold the role.";

export interface TriageCause {
  /** Stable across renders so a list keyed on it does not re-mount every poll. */
  id: string;
  kind: TriageKind;
  programId: ProgramId;
  programName: string;
  /** The role holder to ask. Named because they hold the role, never scored. */
  who?: string;
  /** What is wrong, in one clause. */
  what: string;
  /** How many rows of the headline totals this one cause accounts for, and which total they are in. */
  accounts: { n: number; of: string };
  /** How it is spread: over how long, how concentrated, how old. A level with a direction. */
  spread: string;
  /** What goes wrong if nobody acts. This is what put the cause where it is in the order. */
  risk: string;
  tone: "bad" | "warn";
}

export interface PortfolioTriage {
  causes: TriageCause[];
  /** Set when a linked system stopped answering, because then every count below is a floor. */
  caveat?: string;
  /** How many rows of the four totals the causes account for. */
  covers: { asks: number; overdue: number; cadences: number };
}

export function portfolioTriage(view: Pick<PortfolioView, "programs">): PortfolioTriage {
  const causes: TriageCause[] = [];
  let unreachable = 0;
  let onPrograms = 0;

  for (const p of view.programs) {
    if (p.linked.unreachable > 0) { unreachable += p.linked.unreachable; onPrograms += 1; }
    causes.push(...asksCause(p), ...contractCause(p), ...roleCause(p), ...overdueCause(p), ...p.cadenceMisses.map((m) => cadenceCause(p, m)));
  }

  causes.sort((a, b) => RANK[a.kind] - RANK[b.kind] || b.accounts.n - a.accounts.n || a.programName.localeCompare(b.programName) || a.id.localeCompare(b.id));

  const covers = {
    asks: sum(causes, (c) => (c.kind === "ask" ? c.accounts.n : 0)),
    overdue: sum(causes, (c) => (c.kind === "overdue" ? c.accounts.n : 0)),
    cadences: sum(causes, (c) => (c.kind === "cadence" ? c.accounts.n : 0)),
  };
  const caveat = unreachable > 0
    ? `${plural(unreachable, "linked system")} on ${plural(onPrograms, "contract")} ${unreachable === 1 ? "is" : "are"} not answering, so these counts are a floor.`
    : undefined;
  return { causes, covers, ...(caveat ? { caveat } : {}) };
}

/**
 * An ask still inside the standard is a queue, not a failure, so only the ones past it become a
 * cause. The page may say how many and how old and nothing else about them.
 */
function asksCause(p: PortfolioLine): TriageCause[] {
  if (p.asks.pastStandard === 0) return [];
  return [{
    id: `ask:${p.programId}`, kind: "ask", programId: p.programId, programName: p.name, ...(p.lead ? { who: p.lead } : {}),
    what: `${plural(p.asks.pastStandard, "customer ask")} past the acknowledgement standard`,
    accounts: { n: p.asks.pastStandard, of: "asks unacknowledged" },
    spread: `Oldest ${plural(p.asks.oldestDays, "day")} · the standard is ${p.asks.standard} · ${p.asks.unacknowledged} unacknowledged in all`,
    risk: "The customer asked and has heard nothing back. It is the only one of these they can already see, and an acknowledgement costs a sentence.",
    tone: "bad",
  }];
}

function contractCause(p: PortfolioLine): TriageCause[] {
  if (p.contract.unverified === 0) return [];
  return [{
    id: `contract:${p.programId}`, kind: "contract", programId: p.programId, programName: p.name, ...(p.lead ? { who: p.lead } : {}),
    what: `${plural(p.contract.unverified, "contract event")} nobody has verified`,
    accounts: { n: p.contract.unverified, of: "contract events" },
    spread: `${p.contract.unverified} of ${plural(p.contract.total, "event")} on the contract record · ${p.contract.current} effective in the last 90 days or still ahead`,
    risk: "Modifications and option periods Trellis read from the contract system and nobody confirmed. Scope and the period of performance are being run off an unchecked row.",
    tone: "warn",
  }];
}

/** The lead itself, not the deputy: "Deputy delivery lead" contains the words and is not it. */
const isDeliveryLead = (roleName: string) => /^delivery lead$/i.test(roleName);

/**
 * Only the delivery lead. The other unfilled roles are a structural count that does not move from
 * one week to the next, and they stay in the table column where they already are.
 */
function roleCause(p: PortfolioLine): TriageCause[] {
  if (!p.roles.unfilled.some(isDeliveryLead)) return [];
  return [{
    id: `role:${p.programId}`, kind: "role", programId: p.programId, programName: p.name,
    what: "No delivery lead",
    accounts: { n: 1, of: "roles unfilled" },
    spread: `${p.roles.filled} of ${p.roles.total} roles named`,
    risk: "Nobody is accountable for this contract, and every cadence that runs on the lead cannot open at all. Naming one is a sponsor's call.",
    tone: "bad",
  }];
}

function overdueCause(p: PortfolioLine): TriageCause[] {
  if (p.overdueKinds.work === 0) return [];
  // Not "counted below": the cadence figure only reaches back over its window, and an older
  // open cadence item is overdue without being one of the misses.
  const bookkeeping = p.overdueKinds.cadence > 0
    ? ` · ${p.overdueKinds.cadence} of the ${p.overdue} overdue are cadences the model opened, not promises`
    : "";
  return [{
    id: `overdue:${p.programId}`, kind: "overdue", programId: p.programId, programName: p.name, ...(p.lead ? { who: p.lead } : {}),
    what: `${plural(p.overdueKinds.work, "promise")} past ${p.overdueKinds.work === 1 ? "its" : "their"} date`,
    accounts: { n: p.overdueKinds.work, of: "past their date" },
    spread: `Oldest ${plural(p.overdueOldestDays, "day")}${bookkeeping}`,
    risk: "Work somebody accepted and has not closed. Each has one named owner on the contract, so this is a conversation, not a search.",
    tone: "warn",
  }];
}

/**
 * One rule, one line. Sixty-three missed standups over two days is one decision; the difference
 * between that and sixty-three is the whole reason this page exists. A rule that has never once
 * been recorded as run is not a team that stopped turning up, it is evidence that never arrives.
 */
function cadenceCause(p: PortfolioLine, m: CadenceMiss): TriageCause {
  const never = !m.everRan;
  const concentrated = `${m.missed} missed over ${plural(m.periods, "period")}`;
  return {
    id: `cadence:${p.programId}:${m.cadenceId}`, kind: "cadence", programId: p.programId, programName: p.name,
    what: `${m.name} missed`,
    accounts: { n: m.missed, of: "missed cadences" },
    spread: never
      ? `${concentrated}, ${m.firstPeriod} to ${m.lastPeriod} · no period of this rule has ever been recorded as run`
      : `${concentrated}, ${m.firstPeriod} to ${m.lastPeriod} · ${m.ran} ran in the window`,
    risk: never
      ? "Either this rule does not fit this contract or nothing is reaching Trellis to close it. Both are decisions, and neither of them belongs to the people missing it."
      : m.missedIs === "escalation"
        ? "The model treats a miss of this one as an escalation, not a finding."
        : "The rhythm the contract is run on is not happening. Few periods and many misses means one rule on many units, not many separate lapses.",
    tone: never || m.missedIs === "escalation" ? "bad" : "warn",
  };
}

function sum<T>(xs: T[], f: (x: T) => number): number {
  return xs.reduce((n, x) => n + f(x), 0);
}
